import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ClassroomdisciplineGradeAndFrequenceClassStudentsClassstudentidQueryKey,
  useGetApiV1ClassroomdisciplineWithoutModule,
  usePostApiV1ClassroomdisciplineGradeAndFrequenceClassModulesClassmoduleid,
} from '@/shared/api/generated';

import { bulkLinkAtom, gradeConfigStateAtom } from '../store/atoms/gradeConfigAtom';
import { type Activity, type ClassGroup, EActivityType, type Lesson } from '../types/gradeConfig';
import { nextId } from '../utils/nextId';
import { useDisciplinesSearchDropdown } from './useDisciplinesSearchDropdown';

/** Linha do modal de vínculo em lote — uma **aula** selecionável. */
export interface BulkRow {
  /** Id da aula (`classes[].id`) — o que é enviado no vínculo. */
  readonly id: string;
  readonly disciplineId: string;
  readonly disciplineName: string;
  /** Número sequencial da aula na disciplina (1-based). */
  readonly lessonNumber: number;
  /** Data (ISO) da aula. */
  readonly date: string;
  /** Aula já vinculada à turma (não selecionável). */
  readonly linked: boolean;
  readonly checked: boolean;
}

/** Ids das aulas já vinculadas à turma (para desabilitar no grid). */
function linkedLessonIdSet(classGroup: ClassGroup): Set<string> {
  const s = new Set<string>();
  for (const d of classGroup.disciplines) for (const l of d.lessons) s.add(l.id);
  return s;
}

/** Forma interna de uma linha antes de anotar `linked`/`checked`. */
interface RowData {
  id: string;
  disciplineId: string;
  disciplineName: string;
  lessonNumber: number;
  date: string;
}

/** Aula selecionada, agrupada por disciplina para o update otimista. */
interface OptimisticAula {
  readonly id: string;
  readonly lessonNumber: number;
  readonly date: string;
}

/** Menor data ISO não vazia da lista de aulas (ordenação lexical vale para ISO). */
function minLessonDate(lessons: readonly Lesson[]): string {
  const ds = lessons
    .map(l => l.date)
    .filter(Boolean)
    .sort();
  return ds[0] ?? '';
}

/** Maior data ISO não vazia da lista de aulas. */
function maxLessonDate(lessons: readonly Lesson[]): string {
  const ds = lessons
    .map(l => l.date)
    .filter(Boolean)
    .sort();
  return ds[ds.length - 1] ?? '';
}

/**
 * Aplica o update otimista do vínculo de aulas na working copy da turma: para cada disciplina
 * das aulas selecionadas, cria/mescla a `Discipline` (aulas viram `lessons`) e garante uma
 * `Activity` no módulo destino. É o que atualiza a tabela imediatamente — o `useGradeConfigData`
 * semeia a working copy só uma vez por turma, então a revalidação da query não a rehidrata.
 */
function mergeLinkedAulas(
  classGroup: ClassGroup,
  moduleId: string,
  groups: ReadonlyMap<string, { readonly name: string; readonly aulas: readonly OptimisticAula[] }>,
  lessonName: (n: number) => string,
): ClassGroup {
  const discMap = new Map(classGroup.disciplines.map(d => [d.id, d] as const));
  const activities: Activity[] = [...classGroup.activities];

  for (const [disciplineId, group] of groups) {
    const newLessons: Lesson[] = group.aulas.map(a => ({
      id: a.id,
      name: lessonName(a.lessonNumber),
      date: a.date,
      showInDocument: true,
      countsInGrade: true,
      countsInAttendance: true,
      attendanceByView: true,
      exam: { id: `${a.id}_exam`, name: '', weight: 0 },
    }));

    const existing = discMap.get(disciplineId);
    if (existing) {
      const have = new Set(existing.lessons.map(l => l.id));
      const lessons = [...existing.lessons, ...newLessons.filter(l => !have.has(l.id))];
      discMap.set(disciplineId, {
        ...existing,
        lessons,
        startDate: minLessonDate(lessons),
        endDate: maxLessonDate(lessons),
      });
    } else {
      discMap.set(disciplineId, {
        id: disciplineId,
        name: group.name,
        startDate: minLessonDate(newLessons),
        endDate: maxLessonDate(newLessons),
        lessons: newLessons,
      });
    }

    const disc = discMap.get(disciplineId);
    const hasActivity = activities.some(
      a => a.refId === disciplineId && (a.moduleId ?? '') === moduleId,
    );
    if (disc && !hasActivity) {
      activities.push({
        id: nextId('a'),
        type: EActivityType.Discipline,
        refId: disciplineId,
        moduleId: moduleId || null,
        showInDocument: true,
        countsInGrade: true,
        countsInAttendance: true,
        attendanceByView: true,
        closing: disc.lessons.length === 1 ? 'curso' : 'disciplina',
        moduleWeight: 0,
        defaultGrade: null,
        defaultAttendance: null,
      });
    }
  }

  return { ...classGroup, disciplines: [...discMap.values()], activities };
}

/**
 * Hook do modal de vínculo em lote. Usa **dois endpoints**:
 * - **Busca (autocomplete):** `class-planning shared/disciplines` (`useDisciplinesSearchDropdown`)
 *   localiza a disciplina por nome; o `key` do `DropDownListViewModel` é o id usado adiante.
 * - **Listagem (grid):** `GET classroomdiscipline/without-module`, consultada **apenas** quando o
 *   operador clica em "Buscar" (`runSearch` define `bulk.listingReq`); retorna a disciplina sem
 *   módulo e suas aulas (`DisciplineClassesViewModel`), exibida como uma linha selecionável.
 *
 * A confirmação chama `POST grade-and-frequence/class-modules/{classModuleId}` ("Anexar Aulas
 * ao Módulo"): o corpo `classroomDisciplineIds` recebe os ids das **aulas** (`classes[].id`) das
 * disciplinas selecionadas — não o id da disciplina. Cada disciplina contribui com todas as
 * suas aulas retornadas pela listagem. Ao final, revalida a configuração da turma. Encapsula o
 * `bulkLinkAtom`.
 *
 * NOTA: o `key` do `shared/disciplines` é o `disciplineId` usado como query da listagem
 * `without-module`; os ids do vínculo vêm de `classes[].id` dessa listagem. O período
 * (`from`/`to`) é enviado como `startDate`/`endDate` da listagem.
 */
export function useBulkLink() {
  const { t } = useTranslation('gradeAttendanceConfig');
  const [bulk, setBulk] = useAtom(bulkLinkAtom);
  const [state, setState] = useAtom(gradeConfigStateAtom);
  const queryClient = useQueryClient();
  const linkMutation = usePostApiV1ClassroomdisciplineGradeAndFrequenceClassModulesClassmoduleid();

  const classGroup = useMemo(
    () => state.classGroups.find(c => c.id === state.selectedClassId),
    [state.classGroups, state.selectedClassId],
  );

  // Autocomplete de busca da disciplina (shared/disciplines).
  const { disciplines, isQuerySuccess } = useDisciplinesSearchDropdown(bulk.search);

  // Listagem (grid) — só consulta quando `listingReq` está definido (após clicar em "Buscar").
  const listingQuery = useGetApiV1ClassroomdisciplineWithoutModule(
    bulk.listingReq
      ? {
          disciplineId: bulk.listingReq.disciplineId,
          ...(bulk.listingReq.startDate ? { startDate: bulk.listingReq.startDate } : {}),
          ...(bulk.listingReq.endDate ? { endDate: bulk.listingReq.endDate } : {}),
        }
      : undefined,
    { query: { enabled: bulk.open && bulk.listingReq !== null } },
  );

  const open = useCallback(() => {
    setBulk({
      open: true,
      from: '',
      to: '',
      search: '',
      pickedId: '',
      pickedName: '',
      listingReq: null,
      selected: {},
      module: classGroup?.modules[0]?.id ?? '',
    });
  }, [setBulk, classGroup]);

  const close = useCallback(() => setBulk(prev => ({ ...prev, open: false })), [setBulk]);
  const setFrom = useCallback((from: string) => setBulk(prev => ({ ...prev, from })), [setBulk]);
  const setTo = useCallback((to: string) => setBulk(prev => ({ ...prev, to })), [setBulk]);
  const setSearch = useCallback(
    (search: string) => setBulk(prev => ({ ...prev, search })),
    [setBulk],
  );
  /** Registra a disciplina escolhida no autocomplete (fornece o `disciplineId` da listagem). */
  const setPicked = useCallback(
    (id: string, name: string) =>
      setBulk(prev => ({ ...prev, pickedId: id, pickedName: name, search: name })),
    [setBulk],
  );
  const setModule = useCallback(
    (module: string) => setBulk(prev => ({ ...prev, module })),
    [setBulk],
  );
  const clear = useCallback(
    () =>
      setBulk(prev => ({
        ...prev,
        from: '',
        to: '',
        search: '',
        pickedId: '',
        pickedName: '',
        listingReq: null,
      })),
    [setBulk],
  );

  /** `true` quando o intervalo de datas é inválido (data inicial maior que a final). */
  const isDateRangeInvalid = !!bulk.from && !!bulk.to && bulk.from > bulk.to;

  /** Aciona a listagem (`without-module`) para a disciplina escolhida e o período informado. */
  const runSearch = useCallback(() => {
    const disciplineId = Number(bulk.pickedId);
    if (!Number.isFinite(disciplineId) || disciplineId <= 0) return;
    // Intervalo inválido (início > fim) não dispara a busca — defesa em profundidade.
    if (!!bulk.from && !!bulk.to && bulk.from > bulk.to) return;
    setBulk(prev => ({
      ...prev,
      listingReq: {
        disciplineId,
        ...(prev.from ? { startDate: prev.from } : {}),
        ...(prev.to ? { endDate: prev.to } : {}),
      },
    }));
  }, [bulk.pickedId, bulk.from, bulk.to, setBulk]);

  const toggle = useCallback(
    (row: RowData) =>
      setBulk(prev => {
        const selected = { ...prev.selected };
        if (selected[row.id]) delete selected[row.id];
        else
          selected[row.id] = {
            id: row.id,
            disciplineId: row.disciplineId,
            disciplineName: row.disciplineName,
            lessonNumber: row.lessonNumber,
            date: row.date,
          };
        return { ...prev, selected };
      }),
    [setBulk],
  );

  const linkedLessonIds = useMemo(
    () => (classGroup ? linkedLessonIdSet(classGroup) : new Set<string>()),
    [classGroup],
  );

  /** Aulas da disciplina retornada pela listagem `without-module`, uma linha por aula. */
  const listingRows = useMemo<RowData[]>(() => {
    const vm = listingQuery.data;
    // Resposta sem disciplina (sem id e sem nome) = nenhuma ocorrência encontrada.
    if (!vm || (vm.id == null && !vm.discipline)) return [];
    const disciplineId = String(vm.id ?? bulk.pickedId);
    const disciplineName = vm.discipline ?? bulk.pickedName;
    const out: RowData[] = [];
    for (const c of vm.classes ?? []) {
      if (typeof c.id !== 'number') continue; // aula sem id não é vinculável
      out.push({
        id: String(c.id),
        disciplineId,
        disciplineName,
        lessonNumber: out.length + 1,
        date: c.startDate ?? '',
      });
    }
    return out;
  }, [listingQuery.data, bulk.pickedId, bulk.pickedName]);

  const rows = useMemo<BulkRow[]>(() => {
    // A listagem atual define a ordem (estável ao marcar/desmarcar). Aulas selecionadas em
    // buscas anteriores que não estão na listagem atual são anexadas ao final.
    const merged = new Map<string, RowData>();
    for (const r of listingRows) merged.set(r.id, r);
    for (const o of Object.values(bulk.selected)) {
      if (merged.has(o.id)) continue;
      merged.set(o.id, {
        id: o.id,
        disciplineId: o.disciplineId,
        disciplineName: o.disciplineName,
        lessonNumber: o.lessonNumber,
        date: o.date,
      });
    }
    return Array.from(merged.values()).map(d => ({
      ...d,
      linked: linkedLessonIds.has(d.id),
      checked: !!bulk.selected[d.id],
    }));
  }, [listingRows, linkedLessonIds, bulk.selected]);

  const selectableRows = useMemo(() => rows.filter(r => !r.linked), [rows]);

  const allChecked = selectableRows.length > 0 && selectableRows.every(r => bulk.selected[r.id]);

  const selectedRows = useMemo(() => rows.filter(r => r.checked && !r.linked), [rows]);
  const selectedCount = selectedRows.length;

  const toggleAll = useCallback(() => {
    setBulk(prev => {
      const selected = { ...prev.selected };
      if (allChecked) {
        for (const r of selectableRows) delete selected[r.id];
      } else {
        for (const r of selectableRows)
          selected[r.id] = {
            id: r.id,
            disciplineId: r.disciplineId,
            disciplineName: r.disciplineName,
            lessonNumber: r.lessonNumber,
            date: r.date,
          };
      }
      return { ...prev, selected };
    });
  }, [setBulk, selectableRows, allChecked]);

  const moduleOptions = useMemo(
    () => classGroup?.modules.map(m => ({ value: m.id, label: m.name })) ?? [],
    [classGroup],
  );

  const confirm = useCallback(() => {
    if (!classGroup || !bulk.module || selectedRows.length === 0) {
      setBulk(prev => ({ ...prev, open: false }));
      return;
    }
    const classModuleId = Number(bulk.module);
    // O vínculo "Anexar Aulas ao Módulo" recebe os ids das AULAS (`classes[].id`) selecionadas
    // — cada linha do grid já é uma aula, então o id da linha é o id da aula.
    const ids = selectedRows.map(r => Number(r.id)).filter(n => Number.isFinite(n));
    if (ids.length === 0) {
      setBulk(prev => ({ ...prev, open: false }));
      return;
    }

    linkMutation.mutate(
      { classModuleId, data: { classroomDisciplineIds: ids } },
      {
        onSuccess: () => {
          if (state.selectedClassId) {
            queryClient.invalidateQueries({
              queryKey:
                getApiV1ClassroomdisciplineGradeAndFrequenceClassStudentsClassstudentidQueryKey(
                  Number(state.selectedClassId),
                ),
            });
          }
        },
      },
    );

    // Update otimista: agrupa as aulas selecionadas por disciplina e mescla na working copy.
    const groups = new Map<string, { name: string; aulas: OptimisticAula[] }>();
    for (const r of selectedRows) {
      const g = groups.get(r.disciplineId) ?? { name: r.disciplineName, aulas: [] };
      g.aulas.push({ id: r.id, lessonNumber: r.lessonNumber, date: r.date });
      groups.set(r.disciplineId, g);
    }
    const lessonName = (n: number) => t('bulk.lessonLabel', { number: n });

    setState(prev => ({
      ...prev,
      classGroups: prev.classGroups.map(c =>
        c.id === classGroup.id ? mergeLinkedAulas(c, bulk.module, groups, lessonName) : c,
      ),
    }));
    setBulk(prev => ({ ...prev, open: false, selected: {} }));
  }, [
    classGroup,
    bulk.module,
    selectedRows,
    linkMutation,
    queryClient,
    state.selectedClassId,
    setState,
    setBulk,
    t,
  ]);

  return {
    open: bulk.open,
    from: bulk.from,
    to: bulk.to,
    search: bulk.search,
    picked: { id: bulk.pickedId, name: bulk.pickedName },
    module: bulk.module,
    rows,
    /** Sugestões da disciplina para o autocomplete (shared/disciplines). */
    searchResults: disciplines,
    isQuerySuccess,
    /** `true` após o operador acionar "Buscar" ao menos uma vez. */
    hasSearched: bulk.listingReq !== null,
    isListingFetching: listingQuery.isFetching,
    /** `true` quando o período informado é inválido (data inicial maior que a final). */
    isDateRangeInvalid,
    allChecked,
    selectedCount,
    moduleOptions,
    isLinking: linkMutation.isPending,
    openModal: open,
    close,
    setFrom,
    setTo,
    setSearch,
    setPicked,
    setModule,
    clear,
    runSearch,
    toggle,
    toggleAll,
    confirm,
  } as const;
}
